import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import {
  ClientService,
  QuestionService,
  QuestionTableAnswersByQuestions,
  TableAnswersByQuestionIdAndTaxReturnIds,
} from "../../../../../swaggerServices";
import {
  IFormField,
  makeControlForQuestion,
} from "../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { initTableForSingleTaxReturn } from "../../get-full-tax-return";
import { CurrentBatchService } from "../../../../shared/services/current-batch.service";
import { TenantConfigService } from '../../../../shared/services/tenant-config.service';
import { ConfigStateService } from '../../../config/config-state.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';

declare var $: any;
const el = elementRef => $(elementRef.nativeElement);

@Component({
    selector: 'app-batch-overrides',
    templateUrl: './batch-overrides.component.html',
    styleUrls: ['./batch-overrides.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'tax-returns', alias: 'taxReturns' },
        }
    ],
    standalone: false
})
export class BatchOverridesComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  batchId: number;
  taxReturnStructure: {};
  selectedQuestion: {
    id,
    question,
    isTableRelated,
    taxReturns,
  } = null;
  listOfOverrideTables: {subGroup: any, formGroup: any, header: any, visibleRows: any, hasMoreRows: boolean}[];
  visibleOverrideTables: {subGroup: any, formGroup: any, header: any, visibleRows: any, hasMoreRows: boolean}[];
  visibleQueue = [];
  hasMoreTables: boolean = false;
  markedQuestions: {}; // { questionId: TRUE|FALSE }
  selectedAnswer = null;
  answersById: {};
  ROW_PAGE_SIZE = 20;
  TABLE_PAGE_SIZE = 20;
  uiConfig: TenantConfigService;
  taxpayerAlias: string;
  constructor(
    private activatedRoute: ActivatedRoute,
    private clientService: ClientService,
    private questionService: QuestionService,
    private currentBatchService: CurrentBatchService,
    public config: ConfigStateService,
  ) { }

  ngOnInit() {
    this.subscriptions.push(
      this.activatedRoute.params.subscribe(params => {
        if (params.batchId) {
          const didChange = ((!this.batchId) || this.batchId !== params.batchId);
          this.batchId = params.batchId;
          if (didChange) {
            this.getOverridesForBatch(this.batchId);
          }
        }
      })
    );
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxpayerAlias = this.uiConfig.config.taxpayerAlias;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getOverridesForBatch(batchId) {
    this.markedQuestions = this.getMarkedQuestions();

    const maybeCached = this.currentBatchService.getOverridesForBatch(batchId);
    if (maybeCached) {
      this.initialiseWithOverrides(maybeCached);
    } else {
      this.subscriptions.push(
        this.clientService.getOverridesByBatchId(this.batchId).subscribe(res => {
          this.initialiseWithOverrides(res);
        })
      );
      this.markedQuestions = this.getMarkedQuestions();
    }
  }

  initialiseWithOverrides(overrides) {
    const taxReturnStructure = overrides.taxReturnStructure;
    this.normaliseStructure({
      taxReturnStructure: overrides.taxReturnStructure,
      questions: overrides.questions,
      answers: overrides.answers,
      taxReturns: overrides.taxReturns,
    });

    this.taxReturnStructure = taxReturnStructure;

    this.answersById = {};
    overrides.answers.forEach(answer => {
      this.answersById[answer.id] = answer;
    });
    this.markedQuestions = this.getMarkedQuestions();
  }

  normaliseStructure({taxReturnStructure, questions, answers, taxReturns}) {
    const questionsById = {};
    questions.forEach(q => {
      questionsById[q.id] = q;
    });
    const taxReturnsById = {};
    taxReturns.forEach(tr => {
      taxReturnsById[tr.id] = tr;
    });

    const { byQuestionId: answerWithAuditsByQuestionId } = this.answerAuditsLookUp(answers);

    taxReturnStructure.forEach(category => {
      category.subcategories.forEach(subcategory => {
        const visibleQuestions = subcategory.questions.filter(q => questionsById[q.id]);
        subcategory.questions = visibleQuestions.map(q => questionsById[q.id]) || [];
        subcategory.questions.forEach(q => {
          const maybeAnswersWithAudits = answerWithAuditsByQuestionId[q.id];
          q.taxReturns = [];
          if (maybeAnswersWithAudits) {
            const relatedTaxReturnIds = maybeAnswersWithAudits.map(answer => answer.taxReturnId);
            const relatedTaxReturns = relatedTaxReturnIds.map(trId => taxReturnsById[trId]).sort((a,b) => a.id - b.id);
            q.taxReturns = relatedTaxReturns;
            q.answersWithAudits = maybeAnswersWithAudits;
          }
        });
        subcategory.isVisible = subcategory.questions.length;
      });

      category.isVisible = category.subcategories && category.subcategories.some(sc => sc.isVisible);
    });

    return taxReturnStructure;
  }

  answerAuditsLookUp(answers) {
    const byQuestionId = {};
    answers.forEach(answer => {
      if (!byQuestionId[answer.questionId]) {
        byQuestionId[answer.questionId] = [];
      }
      byQuestionId[answer.questionId].push(answer);
    });

    return {
      byQuestionId,
    };
  }

  selectQuestion(question) {
    if (this.selectedQuestion && this.selectedQuestion.id === question.id) { return; }

    this.selectedAnswer = null;
    this.selectedQuestion = question;
    this.listOfOverrideTables = [];

    if (question.parentId) {
      this.selectedQuestion.isTableRelated = true;
      this.createOverridesTableForTableAnswers(this.selectedQuestion, this.selectedQuestion.taxReturns);
    } else {
      const subGroupTable = this.createOverridesTableForTaxPayers(this.selectedQuestion, this.selectedQuestion.taxReturns);
      const formGroup = this.buildFormForSubGroup(subGroupTable);
      const visibleRows = subGroupTable.rows.slice(0, this.ROW_PAGE_SIZE);

      this.listOfOverrideTables.push({
        header: {
          label: `${this.taxpayerAlias}s`,
        },
        subGroup: subGroupTable,
        formGroup,
        visibleRows,
        hasMoreRows: (subGroupTable.rows.length > visibleRows.length),
      });
      // in this case there is only one table
      this.visibleOverrideTables = this.listOfOverrideTables;
      this.hasMoreTables = false;
    }
  }

  loadMoreRows(overridesTable) {
    const numVisibleRows = overridesTable.visibleRows.length;
    const visibleRows = overridesTable.subGroup.rows.slice(0, numVisibleRows + this.ROW_PAGE_SIZE);
    overridesTable.visibleRows = visibleRows;
    overridesTable.hasMoreRows = (overridesTable.subGroup.rows.length > visibleRows.length);
  }

  loadMoreTables() {
    const currentQuestion: any = this.selectedQuestion;
    this.hasMoreTables = false; // don't allow the user to load more whilst we're already loading more

    // Incrementally render the lists. This will unblock the UI thread intermittently and make the UI feel snappy
    // the user can't see all the tables in one go anyway, and its nicer to give a faster response back.
    const numVisibleTables = this.visibleOverrideTables.length;
    const initialList = this.listOfOverrideTables.slice(numVisibleTables, numVisibleTables + this.TABLE_PAGE_SIZE);
    const progressivelyRender = (index) => {
      if (this.selectedQuestion.id !== currentQuestion.id) { return; }
      if (index >= initialList.length) {
        this.hasMoreTables = (this.listOfOverrideTables.length > this.visibleOverrideTables.length);
        return;
      }

      const item = initialList[index];
      this.visibleOverrideTables.push(item);

      setTimeout(() => {
        progressivelyRender(index+1);
      }, 100);
    };
    progressivelyRender(0);
  }

  createFakeQuestionsForTable(question) {
    const parentQuestion = {
      id: question.id,
      displayField: `${this.taxpayerAlias}s`,
      parentId: null,
      hidden: false,
      dataType: "table"
    };

    const atlasIdQuestion = {
      id: question.id + 1,
      displayFieldType: 'text',
      question: 'Atlas Id',
      hidden: false,
      parentId: parentQuestion.id,
      dataType: "string"
    };

    const firstNameQuestion = {
      id: question.id + 2,
      displayFieldType: 'text',
      question: 'First Name',
      hidden: false,
      parentId: parentQuestion.id,
      dataType: "string"
    };

    const lastNameQuestion = {
      id: question.id + 3,
      displayFieldType: 'text',
      question: 'Last Name',
      hidden: false,
      parentId: parentQuestion.id,
      dataType: "string"
    };

    const actualQuestion = {
      id: question.id + 4,
      displayFieldType: 'text',
      question: question.question,
      hidden: false,
      parentId: parentQuestion.id,
      dataType: "string"
    };

    const rowIdQuestion = {
      id: question.id + 5,
      displayFieldType: 'text',
      question: 'Row ID',
      hidden: true,
      parentId: parentQuestion.id,
      dataType: "string"
    };

    return [parentQuestion, atlasIdQuestion, firstNameQuestion, lastNameQuestion, actualQuestion, rowIdQuestion];
  }

  createOverridesTableForTaxPayers(question, taxReturns): {
    id: number
    question: IFormField
    name: string
    isTable: boolean
    questions: IFormField[]
    rows: Array<{}>
  } {
    // NB: we create 'fake' questions, which allow us to leverage the table UI component
    const [parentQuestion, atlasIdQuestion, firstNameQuestion, lastNameQuestion, actualQuestion, rowIdQuestion] = this.createFakeQuestionsForTable(question);

    const answersByTaxReturnId = {};
    const answersWithAudits = question.answersWithAudits || [];
    answersWithAudits.forEach(awa => {
      answersByTaxReturnId[awa.taxReturnId] = awa;
    });

    let cellId = 1;  // 'unique id' for each cell in this table we're creating
    const createCellsForRow = (tr, row) => {
      const rowId = row.id;

      const createCell = (questionId, answer, isOverride = false) => {
        return {
          id: cellId++,
          answer,
          overrideStatus: isOverride ? 1: 0,
          parentAnswerId: rowId,
          questionId: questionId,
          rowId
        };
      };

      const answerWithAudit = answersByTaxReturnId[tr.id];

      const cells = [];
      cells.push(createCell(atlasIdQuestion.id, tr.atlasId));
      cells.push(createCell(firstNameQuestion.id, tr.firstName));
      cells.push(createCell(lastNameQuestion.id, tr.lastName));
      cells.push(createCell(actualQuestion.id, answerWithAudit ? (answerWithAudit.answer || "") : "", true));
      return cells;
    }

    let rowId = 1;  // 'unique id' for each row in this table we're creating
    const subGroupRows = [];
    taxReturns.forEach(tr => {
      const answerWithAudit = answersByTaxReturnId[tr.id];

      const row = {
        id: rowId++,
        answer: '',
        overrideStatus: 1,
        parentAnswerId: null,
        questionId: parentQuestion.id,
        relevantAnswerId: answerWithAudit.id,
      };

      const cells = createCellsForRow(tr, row);
      const rowCellsByQuestionId = {};
      cells.forEach(rc => {
        rowCellsByQuestionId[rc.questionId] = rc;
      });

      subGroupRows.push({
        cells: rowCellsByQuestionId,
        rowAnswer: row,
      });
    });

    const result = {
      id: parentQuestion.id,
      question: parentQuestion,
      name: `${this.taxpayerAlias}s`,
      isTable: true,
      questions: [atlasIdQuestion, firstNameQuestion, lastNameQuestion, actualQuestion, rowIdQuestion],
      rows: subGroupRows,
    };
    return result;
  }

  buildFormForSubGroup(sg: any) {
    const formGroup = new UntypedFormGroup({});
    const nonTableQuestions = [];
    const seenQuestionIds = new Set();
    if (sg.isTable && !seenQuestionIds.has(sg.question.id)) {
      seenQuestionIds.add(sg.question.id);
      const colQuestions = sg.questions;
      colQuestions.forEach(colQ => {
        if (!seenQuestionIds.has(colQ.id)) {
          seenQuestionIds.add(colQ.id);
          sg.rows.forEach((row, rowIndex) => {
            const cellAnswer = row.cells[colQ.id];
            const control = makeControlForQuestion(colQ, true, cellAnswer);
            formGroup.addControl(`${colQ.id}-${rowIndex}`, control);
            if (control.dirty && !formGroup.dirty) formGroup.markAsDirty();
          });
        }
      });
    }

    sg.questions.forEach(q => {
      if (!seenQuestionIds.has(q.id)) {
        nonTableQuestions.push(q);
        seenQuestionIds.add(q.id);
      }
    });

    nonTableQuestions.forEach(q => {
      const control = makeControlForQuestion(q);
      formGroup.addControl(q.id, control);
      if (control.dirty && !formGroup.dirty) formGroup.markAsDirty();
    });
    return formGroup;
  }

  createOverridesTableForTableAnswers(question: any, taxReturns: any[]) {
    const uniqueTaxReturnIds = taxReturns.map(tr => tr.id).filter((value, index, self) => self.indexOf(value) === index);
    const queryParams: TableAnswersByQuestionIdAndTaxReturnIds = {
      parentId: question.parentId,
      taxReturnIds: uniqueTaxReturnIds,
    };

    this.subscriptions.push(
      this.questionService.getTableAnswersByQuestionIdAndTaxReturnIds(queryParams).subscribe((response: QuestionTableAnswersByQuestions) => {
        if (this.selectedQuestion.id !== question.id) { return; }

        const parentQ = response.questions.find(q => q.id === question.parentId);
        const answersWithAudits = question.answersWithAudits;
        const answerIds = answersWithAudits.map(answer => answer.id);

        this.listOfOverrideTables = [];
        response.taxReturns.forEach((tr, index) => {
          tr.questions = response.questions;
          const subGroup = initTableForSingleTaxReturn(tr, parentQ, response.answersByQuestionIdParentAnswerIdAndTaxReturnId);

          // filter out rows which aren't don't contain answers which have overrides 
          const rows = subGroup.rows.filter(row => {
            const rowContainsRelevantAnswer = Object.values(row.cells).some((a: any) => answerIds.includes(a.id));
            return rowContainsRelevantAnswer;
          });
          // add answerId metadata so we can do quick look-ups later
          rows.forEach(row => {
            const cellWithRelevantAnswer: any = Object.values(row.cells).find((a: any) => answerIds.includes(a.id));
            row.rowAnswer.relevantAnswerId = cellWithRelevantAnswer.id;
          });
          subGroup.rows = rows;
          const formGroup = this.buildFormForSubGroup(subGroup);

          const visibleRows = subGroup.rows.slice(0, this.ROW_PAGE_SIZE);

          this.listOfOverrideTables.push({
            header: {
              label: `${tr.atlasId} ${tr.firstName} ${tr.lastName}`,
            },
            subGroup,
            formGroup,
            visibleRows,
            hasMoreRows: (subGroup.rows.length > visibleRows.length),
          });
        });

        // Incrementally render the lists. This will unblock the UI thread intermittently and make the UI feel snappy
        // the user can't see all the tables in one go anyway, and its nicer to give a faster response back.
        this.visibleOverrideTables = [];
        const initialList = this.listOfOverrideTables.slice(0, this.TABLE_PAGE_SIZE);
        const progressivelyRender = (index) => {
          if (this.selectedQuestion.id !== question.id) { return; }
          if (index >= initialList.length) {
            this.hasMoreTables = (this.listOfOverrideTables.length > this.visibleOverrideTables.length);
            return;
          }

          const item = initialList[index];
          this.visibleOverrideTables.push(item);

          setTimeout(() => {
            progressivelyRender(index+1);
          }, 100);
        };
        progressivelyRender(0);
      })
    );
  }

  gotoTaxReturnQuestionSectionUrl(taxReturn: any, subCategory: any, question: any) {
    return `/tax-returns/${taxReturn.id}/subcategories/${subCategory.id}/question/${question.id}`;
  }

  isBooleanCheckboxColumn(q) {
    return q && q.displayFieldType === 'checkbox' && q.dataType === 'boolean';
  }

  trackByRow(index, rowAnswer) {
    return `${rowAnswer.rowAnswer.questionId}-${rowAnswer.rowAnswer.id}`;
  }

  trackByTable(index, overrideTable) {
    return `${overrideTable.header.label}`;
  }

  markQuestion(question) {
    this.markedQuestions[question.id] = !this.markedQuestions[question.id];

    const key = `overrides-batch-${this.batchId}`;
    window.localStorage.setItem(key, JSON.stringify(this.markedQuestions));
  }

  getMarkedQuestions() {
    const key = `overrides-batch-${this.batchId}`;

    let storedMarks = window.localStorage.getItem(key) || "{}";
    const overridesBatchMarks = JSON.parse(storedMarks);

    return overridesBatchMarks;
  }

  onRowClicked(rowAnswer, overrideTable) {
    const answer = this.answersById[rowAnswer.rowAnswer.relevantAnswerId];
    this.selectedAnswer = {
      ...answer,
      answerAudit: answer.answerAudits[0],
      comment: answer.comments[0],
    };
  }
}
